import { Box, Tooltip } from '@material-ui/core'
import {
  AppointmentTypeDto,
  DEFAULT_BOOKING_WINDOW_DAYS,
  MAX_BOOKING_WINDOW_DAYS,
  MIN_BOOKING_WINDOW_DAYS,
  type CreateAppointmentTypeDto,
} from '@shared/core/appointment-types'
import { resolveRemindersPausedReason } from '@shared/core/billing'
import type { CancellationPolicyFieldsDto, CancellationPolicyMode } from '@shared/core/cancellation-policy'
import type { MessageTemplateDto } from '@shared/core/message-templates'
import classNames from 'classnames'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { PolicyConfirmModal } from 'src/modules/cancellation-policy/components/policy-confirm-modal'
import {
  TypeCancellationPolicyPanel,
  useTypeCancellationPolicyForm,
} from 'src/modules/cancellation-policy/components/type-cancellation-policy-panel'
import { TypeCancellationPolicyRow } from 'src/modules/cancellation-policy/components/type-cancellation-policy-row'
import { describeTypePolicySummary } from 'src/modules/cancellation-policy/utils'
import {
  Button,
  FormControl,
  Icon,
  InfoModal,
  Modal,
  SelectField,
  TextField,
  ToggleSwitch,
  Typography,
} from 'src/modules/core/components'
import { useMessageTemplates } from 'src/modules/message-templates/hooks/use-message-templates'
import { BookingQuestionsEditor, useBookingQuestionsEditor } from 'src/modules/user/components/booking-questions-editor'
import { FINISH_OPEN_QUESTION_MESSAGE } from 'src/modules/user/components/booking-questions-editor/booking-questions-editor.domain'
import { useSetAsDefaultAppointmentType } from 'src/modules/user/hooks/use-appointment-types'
import { useBookingOnboarding } from 'src/modules/user/hooks/use-booking-onboarding'
import { useMeetCapability } from 'src/modules/user/hooks/use-meet-capability'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import {
  BOOKING_WINDOW_HELPER_TEXT,
  BOOKING_WINDOW_LABEL,
  bookingWindowModeOptions,
  bookingWindowToFormData,
  findMissingLocationToken,
  formDataToBookingWindow,
  formDataToLocation,
  formDataToMinutes,
  locationToFormData,
  shouldWarnWindowShorterThanNotice,
  validateMinuteCap,
  type BookingWindowMode,
} from 'src/modules/user/utils/appointment-type-form'
import { TemplateFormDrawer } from '../settings-templates-tab/template-form-drawer'
import { AppointmentTypeLocationField } from './appointment-type-location-field'
import type { AppointmentTypeFormData, AppointmentTypeRightPanel } from './appointment-type-modal.interface'
import classes from './appointment-type-modal.module.scss'
import { AppointmentTypePausedNotice } from './appointment-type-paused-notice'
import { LocationTemplateHint } from './location-template-hint'
import { useAppointmentTypeSave } from './use-appointment-type-save'

interface AppointmentTypeModalProps {
  onClose: () => void
  appointmentTypeId?: string
  initialData?: AppointmentTypeDto
  appointmentTypesCount: number
  defaultAppointmentTypeId?: string
  appointmentTypes?: AppointmentTypeDto[]
}

const defaultValues: AppointmentTypeFormData = {
  name: '',
  calendarId: '',
  duration: 30,
  minimumNotice: 0,
  minimumNoticeUnit: 'minutes',
  bookingWindowMode: 'days',
  bookingWindowDays: DEFAULT_BOOKING_WINDOW_DAYS,
  bufferBefore: 0,
  bufferAfter: 0,
  messageTemplateId: null,
  disableRescheduling: false,
  cancellationPolicyMode: 'account',
  ...locationToFormData(),
}

const TYPE_RESCHEDULE_OFF_NOTE = "Rescheduling is disabled for this Appointment Type, so this doesn't apply."

const noticeUnitOptions = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
]

// Convert minutes to form data (value + unit)
function minutesToFormData(minutes: number): { value: number; unit: 'minutes' | 'hours' | 'days' } {
  if (minutes === 0) {
    return { value: 0, unit: 'minutes' }
  }
  if (minutes % (24 * 60) === 0) {
    return { value: minutes / (24 * 60), unit: 'days' }
  }
  if (minutes % 60 === 0) {
    return { value: minutes / 60, unit: 'hours' }
  }
  return { value: minutes, unit: 'minutes' }
}

export function AppointmentTypeModal({
  onClose,
  appointmentTypeId,
  initialData,
  appointmentTypesCount,
  defaultAppointmentTypeId,
  appointmentTypes = [],
}: AppointmentTypeModalProps) {
  const { data: user } = useProfile()
  const { data: bookingOnboarding } = useBookingOnboarding()
  const calendars = useCalendars()
  const messageTemplates = useMessageTemplates()
  const questionsEditor = useBookingQuestionsEditor(initialData)
  const appointmentTypeSave = useAppointmentTypeSave(appointmentTypeId, questionsEditor)
  const setAsDefaultAppointmentType = useSetAsDefaultAppointmentType()
  const [showDefaultConfirmModal, setShowDefaultConfirmModal] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [editingTemplate, setEditingTemplate] = useState<MessageTemplateDto | null>(null)
  const [rightPanel, setRightPanel] = useState<AppointmentTypeRightPanel>('questions')
  const [pendingPolicySave, setPendingPolicySave] = useState<AppointmentTypeFormData | null>(null)
  const typePolicy = useTypeCancellationPolicyForm({
    typeId: appointmentTypeId,
    initialMode: initialData?.cancellation_policy_mode ?? 'account',
  })
  const { formatMessage } = useIntl()

  const savedTypeId = appointmentTypeId ?? appointmentTypeSave.createdTypeId
  const isEditMode = Boolean(savedTypeId)
  const showDefaultToggle = (isEditMode && appointmentTypesCount >= 2) || (!isEditMode && appointmentTypesCount >= 1)
  const isDefault = Boolean(initialData?.id && initialData.id === defaultAppointmentTypeId)
  const showSaveFirstText = !isEditMode
  const isDefaultToggleReadOnly = !isEditMode || isDefault

  // Sort calendars: selected first (alphabetically), then unselected (alphabetically)
  const sortedCalendars = useMemo(() => {
    if (!calendars.data) return []
    const selected = calendars.data
      .filter((cal) => cal.selected)
      .sort((a, b) => {
        const labelA = (a.title || a.calendar_id || '').toLowerCase()
        const labelB = (b.title || b.calendar_id || '').toLowerCase()
        return labelA.localeCompare(labelB)
      })
    const unselected = calendars.data
      .filter((cal) => !cal.selected)
      .sort((a, b) => {
        const labelA = (a.title || a.calendar_id || '').toLowerCase()
        const labelB = (b.title || b.calendar_id || '').toLowerCase()
        return labelA.localeCompare(labelB)
      })
    return [...selected, ...unselected]
  }, [calendars.data])

  // Create calendar options for SelectField
  const calendarOptions = useMemo(() => {
    return sortedCalendars.map((calendar) => {
      let label = calendar.title || calendar.calendar_id
      if (!calendar.selected) {
        label = `${label} (Not selected)`
      }
      return {
        label,
        value: calendar.id,
        isDisabled: !calendar.selected,
      }
    })
  }, [sortedCalendars])

  // Get default calendar (from initialData or primary/first selected)
  const defaultCalendarId = useMemo(() => {
    if (initialData?.calendar_id) return initialData.calendar_id
    if (!calendars.data) return ''
    const primaryCalendar = calendars.data.find((cal) => cal.primary)
    if (primaryCalendar) return primaryCalendar.id
    const selectedCalendar = calendars.data.find((cal) => cal.selected)
    if (selectedCalendar) return selectedCalendar.id
    return calendars.data[0]?.id || ''
  }, [calendars.data, initialData])

  // Get default message template ID (template with is_default = true)
  const defaultMessageTemplateId = useMemo(() => {
    if (!messageTemplates.data) return null
    const defaultTemplate = messageTemplates.data.find((template) => template.is_default)
    return defaultTemplate?.id || null
  }, [messageTemplates.data])

  // Create message template options for SelectField
  const messageTemplateOptions = useMemo(() => {
    const options = [{ value: null, label: "Don't set reminders automatically" }]
    if (messageTemplates.data) {
      const templateOptions = messageTemplates.data.map((template) => ({
        value: template.id,
        label: template.template_name,
      }))
      options.push(...templateOptions)
    }
    return options
  }, [messageTemplates.data])

  // Convert initial data to form data
  const formDefaultValues = useMemo(() => {
    if (initialData) {
      const { value, unit } = minutesToFormData(initialData.minimum_notice_minutes)
      const bookingWindow = bookingWindowToFormData(initialData.booking_window_days)
      return {
        name: initialData.name,
        calendarId: initialData.calendar_id,
        duration: initialData.duration_minutes,
        minimumNotice: value,
        minimumNoticeUnit: unit,
        bookingWindowMode: bookingWindow.mode,
        bookingWindowDays: bookingWindow.days,
        bufferBefore: initialData.buffer_before_minutes,
        bufferAfter: initialData.buffer_after_minutes,
        messageTemplateId: initialData.set_reminders_using_template_id || null,
        disableRescheduling: !Boolean(initialData.allow_customer_reschedule ?? true),
        cancellationPolicyMode: initialData.cancellation_policy_mode ?? 'account',
        ...locationToFormData(initialData),
      }
    }
    return {
      ...defaultValues,
      calendarId: defaultCalendarId,
      messageTemplateId: defaultMessageTemplateId,
    }
  }, [initialData, defaultCalendarId, defaultMessageTemplateId])

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    getValues,
    reset,
    setValue,
    watch,
  } = useForm<AppointmentTypeFormData>({
    defaultValues: formDefaultValues,
  })
  const remindersPausedReason = user ? resolveRemindersPausedReason(user) : null
  const showPausedNotice = Boolean(remindersPausedReason && watch('messageTemplateId'))

  const calendarId = watch('calendarId')
  const selectedCalendar = calendars.data?.find((calendar) => calendar.id === calendarId)
  const meetCapability = useMeetCapability(calendarId)
  const canHostMeet = meetCapability.isError ? null : (meetCapability.data?.canHostMeet ?? null)

  const selectedTemplate = messageTemplates.data?.find((template) => template.id === watch('messageTemplateId'))
  const missingLocationToken = findMissingLocationToken({
    locationType: watch('locationType'),
    template: selectedTemplate,
  })

  const bookingWindowMode = watch('bookingWindowMode')
  const showBookingWindowWarning = shouldWarnWindowShorterThanNotice(
    formDataToBookingWindow(bookingWindowMode, Number(watch('bookingWindowDays'))),
    formDataToMinutes(Number(watch('minimumNotice')), watch('minimumNoticeUnit')),
  )

  const policyMode = watch('cancellationPolicyMode')
  const isAccountReschedulingOn = Boolean(bookingOnboarding?.is_rescheduling_enabled)
  const reschedulePossible = isAccountReschedulingOn && !watch('disableRescheduling')
  const rescheduleSettingLabel = formatMessage({ id: 'settings.appointment.allowRescheduleFromConfirmation' })
  const policyRescheduleNote = !isAccountReschedulingOn
    ? `This doesn't apply while "${rescheduleSettingLabel}" is off.`
    : reschedulePossible
      ? null
      : TYPE_RESCHEDULE_OFF_NOTE
  const policySummary = describeTypePolicySummary({
    mode: policyMode,
    account: typePolicy.account,
    custom: typePolicy.customRules,
    reschedulePossible,
  })

  const handlePolicyModeChange = (mode: CancellationPolicyMode) => {
    setValue('cancellationPolicyMode', mode, { shouldDirty: true })
    if (mode === 'custom') typePolicy.startCustom()
  }

  // Late defaults (templates loading, "Set as default" in the template editor) must not wipe unsaved edits.
  useEffect(() => {
    if (!isDirty) {
      reset(formDefaultValues)
      return
    }
    if (!dirtyFields.calendarId) setValue('calendarId', formDefaultValues.calendarId)
    if (!dirtyFields.messageTemplateId) setValue('messageTemplateId', formDefaultValues.messageTemplateId)
  }, [formDefaultValues, isDirty, dirtyFields.calendarId, dirtyFields.messageTemplateId, reset, setValue])

  const onSubmit = async (data: AppointmentTypeFormData) => {
    if (!user?.id || !data.calendarId) {
      console.error('Missing user ID or calendar ID')
      return
    }

    if (questionsEditor.openCard) {
      setRightPanel('questions')
      setSaveError(FINISH_OPEN_QUESTION_MESSAGE)
      return
    }

    if (data.cancellationPolicyMode === 'custom' && !typePolicy.validate()) {
      setRightPanel('policy')
      return
    }

    if (appointmentTypeId && typePolicy.hasChanges(data.cancellationPolicyMode)) {
      setPendingPolicySave(data)
      return
    }

    await saveType(data)
  }

  const saveType = async (data: AppointmentTypeFormData) => {
    setSaveError(null)
    const errorMessage = await appointmentTypeSave.save(toAppointmentTypeFields(data, typePolicy.getCustomPolicy()))
    if (errorMessage) setSaveError(errorMessage)
    else onClose()
  }

  const confirmPolicySave = () => {
    const data = pendingPolicySave
    setPendingPolicySave(null)
    if (data) void saveType(data)
  }

  const handleIsDefaultChange = (checked: boolean) => {
    if (checked) {
      setShowDefaultConfirmModal(true)
    }
  }

  const handleConfirmDefault = () => {
    if (savedTypeId) {
      setAsDefaultAppointmentType.mutate(savedTypeId, {
        onSuccess: () => setShowDefaultConfirmModal(false),
      })
    }
  }

  const isLoading = appointmentTypeSave.isSaving
  const copySources = appointmentTypes.filter((appointmentType) => appointmentType.id !== savedTypeId)

  return (
    <>
      <Modal
        onClose={onClose}
        title={
          <Typography variant="h3" className={classes.title}>
            {isEditMode ? 'Edit Appointment Type' : 'Create Appointment Type'}
          </Typography>
        }
        isCentered
        className={classNames(classes.modal, classes.twoColumns)}
        contentClassName={classes.content}
      >
        <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
          <div className={classes.columns}>
            <div className={classes.settingsColumn}>
              {/* Name */}
              <FormControl
                label={
                  showDefaultToggle ? (
                    <div className={classes.nameFieldLabelRow}>
                      <span className={classes.nameFieldLabel}>Name</span>
                      <div className={classes.switchContainer}>
                        <ToggleSwitch
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleIsDefaultChange(e.target.checked)}
                          checked={isDefault}
                          disabled={isDefaultToggleReadOnly}
                        />
                        <Tooltip title="Your default appointment type will be automatically selected on your Booking Page.">
                          <Typography variant="body" className={classes.infoText} element="span">
                            {isDefault ? 'This is the default Appointment Type' : 'Set as default'}
                            {showSaveFirstText && !isDefault && ' (save the appointment type first)'}
                          </Typography>
                        </Tooltip>
                      </div>
                    </div>
                  ) : (
                    'Name'
                  )
                }
                labelClassName={showDefaultToggle ? classes.nameFieldLabelFullWidth : undefined}
                error={errors.name?.message}
                className={classes.fieldWrapper}
              >
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Name is required' }}
                  render={({ field }) => (
                    <TextField
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="e.g., Consultation, Follow-up"
                      className={classes.nameInput}
                    />
                  )}
                />
              </FormControl>

              {/* Line 2: Duration and Minimum Notice */}
              <div className={classes.row}>
                <FormControl
                  label="Duration (minutes)"
                  error={errors.duration?.message}
                  className={classes.fieldWrapper}
                >
                  <Controller
                    name="duration"
                    control={control}
                    rules={{
                      required: 'Duration is required',
                      min: { value: 1, message: 'Duration must be at least 1 minute' },
                      validate: (value) =>
                        validateMinuteCap('duration_minutes', Number(value), initialData?.duration_minutes),
                    }}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className={classes.numberInput}
                      />
                    )}
                  />
                </FormControl>

                <div className={classes.minimumNoticeWrapper}>
                  <FormControl
                    label="Minimum Notice"
                    error={errors.minimumNotice?.message}
                    className={classes.fieldWrapper}
                  >
                    <div className={classes.minimumNoticeInputs}>
                      <Controller
                        name="minimumNotice"
                        control={control}
                        rules={{
                          required: 'Minimum notice is required',
                          min: { value: 0, message: 'Minimum notice cannot be negative' },
                          validate: (value) =>
                            validateMinuteCap(
                              'minimum_notice_minutes',
                              formDataToMinutes(Number(value), getValues('minimumNoticeUnit')),
                              initialData?.minimum_notice_minutes,
                            ),
                        }}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            className={classes.numberInput}
                          />
                        )}
                      />
                      <Controller
                        name="minimumNoticeUnit"
                        control={control}
                        render={({ field }) => (
                          <SelectField
                            value={noticeUnitOptions.find((option) => option.value === field.value) || null}
                            onChange={(option: { value: 'minutes' | 'hours' | 'days'; label: string } | null) =>
                              field.onChange(option?.value || 'minutes')
                            }
                            options={noticeUnitOptions}
                            className={classes.unitSelect}
                          />
                        )}
                      />
                    </div>
                  </FormControl>
                </div>
              </div>

              {/* Booking Window: two named options, never a clearable number field */}
              <div className={classes.row}>
                <FormControl
                  label={BOOKING_WINDOW_LABEL}
                  error={errors.bookingWindowDays?.message}
                  className={classes.fieldWrapper}
                >
                  <div className={classes.minimumNoticeInputs}>
                    <Controller
                      name="bookingWindowMode"
                      control={control}
                      render={({ field }) => (
                        <SelectField
                          value={bookingWindowModeOptions.find((option) => option.value === field.value) || null}
                          onChange={(option: { value: BookingWindowMode; label: string } | null) =>
                            field.onChange(option?.value || 'indefinite')
                          }
                          options={bookingWindowModeOptions}
                        />
                      )}
                    />
                    {bookingWindowMode === 'days' && (
                      <Controller
                        name="bookingWindowDays"
                        control={control}
                        rules={{
                          validate: (value) => {
                            if (bookingWindowMode !== 'days') return true
                            const days = Number(value)
                            if (!Number.isInteger(days)) return 'Enter a whole number of days'
                            if (days < MIN_BOOKING_WINDOW_DAYS || days > MAX_BOOKING_WINDOW_DAYS) {
                              return `Enter a number between ${MIN_BOOKING_WINDOW_DAYS} and ${MAX_BOOKING_WINDOW_DAYS}`
                            }
                            return true
                          },
                        }}
                        render={({ field }) => (
                          <TextField
                            type="number"
                            value={field.value}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            className={classes.numberInput}
                          />
                        )}
                      />
                    )}
                  </div>
                  <Typography variant="body" className={classes.rescheduleSubtext}>
                    {BOOKING_WINDOW_HELPER_TEXT}
                  </Typography>
                  {showBookingWindowWarning && (
                    <Typography variant="body" className={classes.rescheduleSubtext} role="alert">
                      This window closes before your minimum notice elapses, so nothing would be bookable. You can still
                      save.
                    </Typography>
                  )}
                </FormControl>
              </div>

              {/* Line 3: Buffer Before and Buffer After */}
              <div className={classes.row}>
                <FormControl
                  label="Buffer Before (minutes)"
                  error={errors.bufferBefore?.message}
                  className={classes.fieldWrapper}
                >
                  <Controller
                    name="bufferBefore"
                    control={control}
                    rules={{
                      required: 'Buffer before is required',
                      min: { value: 0, message: 'Buffer before cannot be negative' },
                      validate: (value) =>
                        validateMinuteCap('buffer_before_minutes', Number(value), initialData?.buffer_before_minutes),
                    }}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className={classes.numberInput}
                      />
                    )}
                  />
                </FormControl>

                <FormControl
                  label="Buffer After (minutes)"
                  error={errors.bufferAfter?.message}
                  className={classes.fieldWrapper}
                >
                  <Controller
                    name="bufferAfter"
                    control={control}
                    rules={{
                      required: 'Buffer after is required',
                      min: { value: 0, message: 'Buffer after cannot be negative' },
                      validate: (value) =>
                        validateMinuteCap('buffer_after_minutes', Number(value), initialData?.buffer_after_minutes),
                    }}
                    render={({ field }) => (
                      <TextField
                        type="number"
                        value={field.value}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                        className={classes.numberInput}
                      />
                    )}
                  />
                </FormControl>
              </div>

              <AppointmentTypeLocationField
                control={control}
                calendarName={selectedCalendar?.title || selectedCalendar?.calendar_id || ''}
                canHostMeet={canHostMeet}
              />

              {/* Disable rescheduling – only show when booking onboarding is completed */}
              {bookingOnboarding?.onboarding_completed === true && (
                <div className={classes.rescheduleField}>
                  <div className={classes.rescheduleLabelBlock}>
                    <div className={classes.rescheduleLabelRow}>
                      <Typography
                        variant="body"
                        className={
                          bookingOnboarding?.is_rescheduling_enabled ? classes.rescheduleLabelDanger : undefined
                        }
                      >
                        {bookingOnboarding?.is_rescheduling_enabled
                          ? 'Disable rescheduling'
                          : 'Rescheduling is disabled'}
                      </Typography>
                      <Tooltip
                        title={
                          <Typography variant="body" className={classes.tooltipText}>
                            {bookingOnboarding?.is_rescheduling_enabled
                              ? 'By default, customers can reschedule up to the Minimum Notice period before the appointment. Enable this to block rescheduling for this appointment type only.'
                              : 'Rescheduling is currently turned off for all appointments. Enable it in the Other Settings tab to manage it for individual appointment types here.'}
                          </Typography>
                        }
                        placement="top"
                        arrow
                      >
                        <Box component="span" className={classes.tooltipIconContainer}>
                          <Icon name="questionMark" />
                        </Box>
                      </Tooltip>
                    </div>
                    <Typography variant="body" className={classes.rescheduleSubtext}>
                      {bookingOnboarding?.is_rescheduling_enabled
                        ? 'For this Appointment Type, only'
                        : 'Enable rescheduling from the Other Settings tab'}
                    </Typography>
                  </div>
                  <Controller
                    name="disableRescheduling"
                    control={control}
                    render={({ field }) =>
                      bookingOnboarding?.is_rescheduling_enabled ? (
                        <ToggleSwitch checked={field.value} onChange={field.onChange} />
                      ) : null
                    }
                  />
                </div>
              )}

              <TypeCancellationPolicyRow
                summary={policySummary}
                isOpen={rightPanel === 'policy'}
                onOpen={() => setRightPanel('policy')}
              />

              <hr className={classes.separator} />

              {/* Calendar Selection */}
              <FormControl label="Linked Calendar" error={errors.calendarId?.message} className={classes.fieldWrapper}>
                <Controller
                  name="calendarId"
                  control={control}
                  rules={{ required: 'Calendar is required' }}
                  render={({ field }) => (
                    <SelectField
                      value={calendarOptions.find((option) => option.value === field.value) || null}
                      onChange={(option: { value: string; label: string; isDisabled?: boolean } | null) => {
                        if (option && !option.isDisabled) {
                          field.onChange(option.value)
                        }
                      }}
                      options={calendarOptions}
                      isOptionDisabled={(option) => option.isDisabled || false}
                      className={classes.calendarSelect}
                      closeMenuOnSelect
                    />
                  )}
                />
                <Typography variant="body" className={classes.calendarHelperText}>
                  We&apos;ll automatically add appointments to this calendar when customers book with you.
                </Typography>
              </FormControl>

              {/* Message Template Selection */}
              <FormControl
                label="Automatically set reminders using a Message Template"
                error={errors.messageTemplateId?.message}
                className={classes.fieldWrapper}
              >
                <Controller
                  name="messageTemplateId"
                  control={control}
                  render={({ field }) => (
                    <SelectField
                      value={messageTemplateOptions.find((option) => option.value === field.value) || null}
                      onChange={(option: { value: string | null; label: string } | null) => {
                        field.onChange(option?.value || null)
                      }}
                      options={messageTemplateOptions}
                      className={classes.calendarSelect}
                      closeMenuOnSelect
                    />
                  )}
                />
                {selectedTemplate && missingLocationToken && (
                  <LocationTemplateHint
                    templateName={selectedTemplate.template_name}
                    token={missingLocationToken}
                    onEditTemplate={() => setEditingTemplate(selectedTemplate)}
                  />
                )}
                {showPausedNotice && (
                  <AppointmentTypePausedNotice
                    reason={remindersPausedReason}
                    freeRemindersLimit={user.effective_free_reminders_limit}
                  />
                )}
              </FormControl>
            </div>

            <div className={classes.questionsColumn}>
              {/* Hidden, never unmounted: an open question card keeps its draft in local state. */}
              <div
                className={classNames({ [classes.hiddenPanel]: rightPanel === 'policy' })}
                data-testid="booking-questions-panel"
              >
                <BookingQuestionsEditor
                  editor={questionsEditor}
                  copySources={copySources}
                  showAddressField={watch('locationType') === 'customer_address'}
                />
              </div>
              {rightPanel === 'policy' && (
                <TypeCancellationPolicyPanel
                  typeName={watch('name')}
                  mode={policyMode}
                  onModeChange={handlePolicyModeChange}
                  account={typePolicy.account}
                  customControl={typePolicy.form.control}
                  customErrors={typePolicy.errors}
                  onNoticeChange={typePolicy.onNoticeChange}
                  onTextChange={typePolicy.onTextChange}
                  reschedulePossible={reschedulePossible}
                  rescheduleNotApplicableNote={policyRescheduleNote}
                  rescheduleSettingLabel={rescheduleSettingLabel}
                  onClose={() => setRightPanel('questions')}
                />
              )}
            </div>
          </div>

          {saveError && (
            <Typography variant="body" role="alert" className={classes.saveError}>
              {saveError}
            </Typography>
          )}

          <div className={classes.buttons}>
            <Button type="button" onClick={onClose} variant="withBorder" disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </Modal>

      {showDefaultConfirmModal && (
        <InfoModal
          title="Set as default Appointment Type?"
          description={
            <>
              This will update which appointment type is used by default for new bookings.
              {setAsDefaultAppointmentType.error && (
                <div className={classes.modalErrorMessage}>
                  {(setAsDefaultAppointmentType.error as Error)?.message ||
                    'An error occurred while setting the default appointment type.'}
                </div>
              )}
            </>
          }
          button="Confirm"
          closeButton="Cancel"
          onClose={() => setShowDefaultConfirmModal(false)}
          onClick={handleConfirmDefault}
          isLoading={setAsDefaultAppointmentType.isLoading}
          loadingText="Saving..."
        />
      )}

      {pendingPolicySave && (
        <PolicyConfirmModal
          title="Save cancellation policy changes?"
          cancelLabel="Cancel"
          confirmLabel="Save changes"
          onCancel={() => setPendingPolicySave(null)}
          onConfirm={confirmPolicySave}
          testId="type-cancellation-policy-save-warning"
        >
          <p>
            {`The change applies right away to every upcoming "${pendingPolicySave.name}" appointment, including appointments booked before today.`}
          </p>
        </PolicyConfirmModal>
      )}

      {/* Outside the form: React events bubble out of the drawer, and its submit must not save this type. */}
      {editingTemplate && (
        <TemplateFormDrawer isOpen stackOverModal template={editingTemplate} onClose={() => setEditingTemplate(null)} />
      )}
    </>
  )
}

function toAppointmentTypeFields(
  data: AppointmentTypeFormData,
  customPolicy: CancellationPolicyFieldsDto | undefined,
): CreateAppointmentTypeDto {
  return {
    name: data.name,
    calendar_id: data.calendarId,
    duration_minutes: data.duration,
    buffer_before_minutes: data.bufferBefore,
    buffer_after_minutes: data.bufferAfter,
    minimum_notice_minutes: formDataToMinutes(data.minimumNotice, data.minimumNoticeUnit),
    booking_window_days: formDataToBookingWindow(data.bookingWindowMode, Number(data.bookingWindowDays)),
    set_reminders_using_template_id: data.messageTemplateId || null,
    allow_customer_reschedule: !data.disableRescheduling,
    ...formDataToLocation(data),
    cancellation_policy_mode: data.cancellationPolicyMode,
    ...(data.cancellationPolicyMode === 'custom' && customPolicy ? { custom_cancellation_policy: customPolicy } : {}),
  }
}
